import { cloneDeep, isEqual } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import {
  changeProfileField,
  setBillingEqual,
} from "app/store/transmission/slice/transmissionSlice";
import { useSaveMultipleBillingScheduleMutation } from "app/store/transmission/api/transmissionTableApi";
import { showToast } from "utils/toast";
import { useCallback, useEffect, useState } from "react";
import useDebounce from "app/hooks/useDebounce";
import { getDay31BillingCodes, UPDATED_BILLING_CODES } from "static/constants";
import { checkIsIDTFBilling } from "utils/billingCode.util";

const useBillingSchedule = () => {
  const {
    billingSchedule,
    isBillingEqual,
    patientId,
    profile,
    originalBillingSchedule,
  } = useSelector((state) => state.transmission);

  const [selectedCustomBillingCode, setSelectedCustomBillingCode] = useState();
  const [isCustomBillingEnabled, setIsCustomBillingEnabled] = useState(false);

  const [newBillingSchedule, setNewBillingSchedule] = useState({
    day31: false,
    day31BillingCode: "",
    day91: false,
    day91BillingCode: "",
    custom: {
      days: 0,
      enabled: false,
      customBillingCode: "",
    },
  });

  const { accountsMapper } = useSelector((state) => state.account);
  const isIDTFBilling = checkIsIDTFBilling({
    account: accountsMapper[profile?.account],
    patient: profile,
  });

  const dispatch = useDispatch();

  const [
    saveMultipleBillingSchedule,
    { isSuccess, isError, error, data: updatedData },
  ] = useSaveMultipleBillingScheduleMutation();

  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    isError && error && showToast({ message: error?.message, type: "error" });
    if (isSuccess && updatedData) {
      showToast({ message: updatedData?.message });
    }
  }, [updatedData, error]);

  useEffect(() => {
    if (billingSchedule) {
      const copy = cloneDeep(billingSchedule);
      setNewBillingSchedule({
        ...copy,
      });
      handleCustomBillingCodeSelect(copy.custom.customBillingCode, copy);
    }
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      setNewBillingSchedule(cloneDeep(billingSchedule));
    }
  }, [billingSchedule]);

  const handleCustomBillingCodeSelect = (
    customBillingCode,
    billingSchedule
  ) => {
    const copy = cloneDeep(billingSchedule);

    setIsCustomBillingEnabled(copy.custom.enabled);
    setSelectedCustomBillingCode(customBillingCode);

    if (copy.custom?.enabled && customBillingCode) {
      if (copy.day31BillingCode === customBillingCode) {
        copy.day31 = false;
      }

      if (copy.day91BillingCode === customBillingCode) {
        copy.day91 = false;
      }
    }
    copy.custom.customBillingCode = customBillingCode;
    handleInputChange(copy, originalBillingSchedule);
  };

  const handleBillingCycleCheckbox = (is31Day = false) => {
    const copy = cloneDeep(newBillingSchedule);
    if (is31Day) {
      copy.day31 = !newBillingSchedule.day31;

      const isCustomAnd31DaySame =
        isCustomBillingEnabled &&
        copy.day31BillingCode === selectedCustomBillingCode;

      copy.day31BillingCode = isCustomAnd31DaySame ? "" : copy.day31BillingCode;
    } else {
      copy.day91 = !newBillingSchedule.day91;

      const isCustomAnd91DaySame =
        isCustomBillingEnabled &&
        copy.day91BillingCode === selectedCustomBillingCode;

      copy.day91BillingCode = isCustomAnd91DaySame ? "" : copy.day91BillingCode;
    }

    handleInputChange(copy, originalBillingSchedule);
  };

  useEffect(() => {
    if (billingSchedule && originalBillingSchedule) {
      const equal = isEqual(billingSchedule, originalBillingSchedule);
      dispatch(setBillingEqual(equal));
    }
  }, [billingSchedule, dispatch, originalBillingSchedule]);

  const onBillingScheduleChange = async (data, originalBillingSchedule) => {
    const equal = isEqual(data, originalBillingSchedule);
    dispatch(changeProfileField({ type: "billingSchedule", value: data }));
    dispatch(setBillingEqual(equal));
  };

  const debounceDispatch = useCallback(
    useDebounce((updatedSchedule, originalBillingSchedule) => {
      onBillingScheduleChange(updatedSchedule, originalBillingSchedule);
    }, 500),
    []
  );

  const handleInputChange = async (
    updatedSchedule,
    originalBillingSchedule
  ) => {
    setNewBillingSchedule({
      ...updatedSchedule,
    });
    debounceDispatch(updatedSchedule, originalBillingSchedule);
  };

  const handleBillingSchedule = async (originalBillingSchedule) => {
    let payload = {
      patientId,
      billingSchedule: [],
    };

    if (
      originalBillingSchedule.day31 !== billingSchedule.day31 ||
      originalBillingSchedule.day31BillingCode !==
        billingSchedule.day31BillingCode
    ) {
      payload.billingSchedule.push({
        days: 31,
        active: billingSchedule.day31,
        billingCode: billingSchedule.day31BillingCode,
        isCustom: false,
      });
    }
    if (
      originalBillingSchedule.day91 !== billingSchedule.day91 ||
      originalBillingSchedule.day91BillingCode !==
        billingSchedule.day91BillingCode
    ) {
      payload.billingSchedule.push({
        days: 91,
        active: billingSchedule.day91,
        billingCode: billingSchedule.day91BillingCode,
        isCustom: false,
      });
    }
    if (!isEqual(originalBillingSchedule.custom, billingSchedule.custom)) {
      payload.billingSchedule.push({
        days: Number(billingSchedule.custom.days),
        active: billingSchedule.custom.enabled,
        billingCode: billingSchedule.custom.customBillingCode,
        isCustom: true,
      });
    }

    const invalidBillingCode = checkHasInvalidBillingCode(
      payload.billingSchedule
    );

    if (invalidBillingCode) {
      throw new Error(`Invalid billing code ${invalidBillingCode.billingCode}`);
    }

    const response = await saveMultipleBillingSchedule(payload);
    const updatedData = cloneDeep(billingSchedule);

    if (!response.error) {
      dispatch(
        changeProfileField({
          type: "originalBillingSchedule",
          value: updatedData,
        })
      );

      setNewBillingSchedule(updatedData);
    }

    return response;
  };

  /**
   * Checks if active schedule has invalid billingCode
   * @param {*} billingSchedulePayload
   * @returns invalid billingSchedule or null
   */
  const checkHasInvalidBillingCode = (billingSchedulePayload) => {
    let validBillingCodes = [
      ...new Set([
        ...UPDATED_BILLING_CODES,
        ...getDay31BillingCodes(isIDTFBilling),
      ]),
    ];

    const invalidBillingCode = billingSchedulePayload.find(
      (schedule) =>
        schedule.active && !validBillingCodes.includes(schedule.billingCode)
    );
    return invalidBillingCode;
  };

  return {
    profile,
    isEditMode,
    isIDTFBilling,
    isBillingEqual,
    billingSchedule,
    newBillingSchedule,
    isCustomBillingEnabled,
    selectedCustomBillingCode,
    dispatch,
    setIsEditMode,
    handleInputChange,
    handleBillingSchedule,
    onBillingScheduleChange,
    handleBillingCycleCheckbox,
    setSelectedCustomBillingCode,
    handleCustomBillingCodeSelect,
  };
};

export default useBillingSchedule;
